import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@truefit/http-utils';
import { useSnackbar } from 'notistack';

/**
 * Automated-clipping rules (C360-2813).
 *
 * A rule says: for this league, when this SES event arrives and these
 * conditions hold, cut a clip. Rules with `isSystem` are seeded by the
 * system and cannot be deleted; platform rules (`orgId` null — which the
 * seeds are) additionally cannot have their shape edited, only be paused or
 * activated, because they govern every org's games.
 */
export interface ClipRule {
  id: number;
  uniqueId: string;
  name: string;
  description: string | null;
  enabled: boolean;
  isSystem: boolean;
  /** Owning org; null = platform-wide (seeded). Other orgs' rules are never returned. */
  orgId: number | null;
  leagueId: number;
  eventType: string;
  conditions: Record<string, unknown>;
  preRollMs: number;
  postRollMs: number;
  trackTarget: string;
  /** "all" = every live camera, "closest" = nearest camera only. No editor control yet. */
  cameraSelection: string;
  outputProfileId: number | null;
  /** Schedule bounds; null means unbounded on that side ("full season"). */
  activeFrom: string | null;
  activeTo: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  league?: { id: number; name: string | null; displayName: string | null };
  creator?: { id: number; firstName: string | null; lastName: string | null; email: string | null } | null;
}

/**
 * An SES event a league offers to rules, with what a rule on it may track.
 * The object of play is always among `trackedObjects` (business-api appends
 * it, labelled from `league.objectOfPlay`).
 */
export interface LeagueClipEvent {
  value: string;
  label: string;
  trackedObjects: { trackedObject: string; label: string }[];
}

interface ClipRulesResponse {
  success: boolean;
  count: number;
  supportedConditions: string[];
  trackTargets: string[];
  /** The caller org's leagues and the SES events a rule may match in each. */
  leagues: { id: number; clipEventTypes: LeagueClipEvent[] }[];
  clipRules: ClipRule[];
}

export type ClipRuleInput = Partial<
  Pick<
    ClipRule,
    | 'name'
    | 'description'
    | 'enabled'
    | 'leagueId'
    | 'eventType'
    | 'conditions'
    | 'preRollMs'
    | 'postRollMs'
    | 'trackTarget'
    | 'outputProfileId'
    | 'activeFrom'
    | 'activeTo'
  >
>;

export const CLIP_RULES_QUERY_KEY = ['ClipRules'];

export const useClipRulesQuery = () =>
  useQuery({
    queryKey: CLIP_RULES_QUERY_KEY,
    queryFn: async () => {
      const { data } = await get<ClipRulesResponse>('/v1.0/clip-rules');
      return data;
    },
    staleTime: 30 * 1000,
  });

export const useClipRules = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const query = useClipRulesQuery();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: CLIP_RULES_QUERY_KEY });

  const notifyError = (error: any, fallback: string) =>
    enqueueSnackbar(error?.response?.data?.message || error?.message || fallback, { variant: 'error' });

  const createRule = useMutation({
    mutationFn: async (input: ClipRuleInput) => {
      const { data } = await post<{ clipRule: ClipRule }>('/v1.0/clip-rules', input);
      return data.clipRule;
    },
    onSuccess: () => {
      invalidate();
      enqueueSnackbar('Rule created', { variant: 'success' });
    },
    onError: (error: any) => notifyError(error, 'Failed to create rule'),
  });

  const updateRule = useMutation({
    mutationFn: async ({ id, ...input }: ClipRuleInput & { id: number }) => {
      const { data } = await put<{ clipRule: ClipRule }>(`/v1.0/clip-rules/${id}`, input);
      return data.clipRule;
    },
    onSuccess: (_data, variables) => {
      invalidate();
      // Toggling is the common case and deserves an unambiguous message —
      // enabling a rule starts clipping live games within ~13s.
      if (variables.enabled !== undefined) {
        enqueueSnackbar(variables.enabled ? 'Rule enabled' : 'Rule disabled', { variant: 'success' });
      } else {
        enqueueSnackbar('Rule updated', { variant: 'success' });
      }
    },
    onError: (error: any) => notifyError(error, 'Failed to update rule'),
  });

  const deleteRule = useMutation({
    mutationFn: async (id: number) => {
      await del(`/v1.0/clip-rules/${id}`);
      return id;
    },
    onSuccess: () => {
      invalidate();
      enqueueSnackbar('Rule deleted', { variant: 'success' });
    },
    onError: (error: any) => notifyError(error, 'Failed to delete rule'),
  });

  return {
    clipRules: query.data?.clipRules ?? [],
    supportedConditions: query.data?.supportedConditions ?? [],
    trackTargets: query.data?.trackTargets ?? [],
    leagueClipEventTypes: query.data?.leagues ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    createRule,
    updateRule,
    deleteRule,
  };
};

export default useClipRules;
